import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import MicIcon from "@mui/icons-material/Mic"
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import { styles } from "./TeacherMessage.styles"
import { useAuthContext } from "contexts/AuthContext"
import CloseIcon from "@mui/icons-material/Close"
import { startRecordingBip, stopRecordingBip } from "utils/sounds"
import useSound from "use-sound"
import useRecording from "hooks/useRecording"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import EmojiPicker from "./EmojiPicker"
import KeyPicker from "./KeyPicker"
import { useModuleContext } from "contexts/ModuleContext"
import { SCREEN_TYPES, SUPPORT_TYPES } from "common/constants"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import html2canvas from "html2canvas"
import useApi from "modules/technicalSupport"
import useGradeApi from "modules/student/grades"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import TextToolBar from "components/ActivityContent/TextComprehesion/TextToolBar"

export default function TeacherMessage({
  open,
  onClose,
  takeScreenshot = false,
}) {
  const { t } = useTranslation()
  const {
    viewMode,
    studentAnswers,
    activity,
    exercise,
    getActivityExercises,
    module,
    setModule,
    setActivities,
    activityIndex,
    activities,
  } = useModuleContext()
  const { selectedGroupData, userData, audioSettings } = useAuthContext()
  const { addTechnicalSupport, isFetching } = useApi()
  const { updateGrade } = useGradeApi()
  const [playStart] = useSound(startRecordingBip, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playStop] = useSound(stopRecordingBip, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [loadingScreenshot, setLoadingScreenshot] = useState(false)
  const [currentRecording, setCurrentRecording] = useState(null)
  const [attachedImage, setAttachedImage] = React.useState(null)
  const [newGrade, setNewGrade] = useState("")
  const [
    recordingURL,
    isRecording,
    startRecording,
    stopRecording,
    initStream,
    resetRecorder,
  ] = useRecording()
  const [screenshot, setScreenshot] = React.useState(null)
  const inputRef = useRef(null)
  const MySwal = withReactContent(Swal)
  const [fontSettings, setFontSettings] = useState({
    fontSize: undefined,
    fontFamily: undefined,
    bgColor: undefined,
  })

  useEffect(() => {
    setLoadingScreenshot(false)
    setScreenshot(null)
    setCurrentRecording(null)
    setAttachedImage(null)
    setNewGrade("")
    return () => {
      resetRecorder(initStream)
    }
  }, [open])

  useEffect(() => {
    if (!takeScreenshot) return
    setLoadingScreenshot(true)
    setNewGrade("")
    setCurrentRecording(null)
    setAttachedImage(null)
    if (inputRef.current) inputRef.current.value = ""
    html2canvas(document.body, {
      allowTaint: true,
      useCORS: true,
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: 0,
      scrollY: -window.scrollY,
    }).then((canvas) => {
      try {
        const image = canvas.toDataURL("image/png")
        setScreenshot(image)
      } catch (error) {
        console.log(`Error html2canvas: ${error}`)
      } finally {
        setLoadingScreenshot(false)
      }
    })
  }, [takeScreenshot])

  useEffect(() => {
    if (!recordingURL || recordingURL === null) return
    setCurrentRecording(URL.createObjectURL(recordingURL))
  }, [recordingURL])

  const recordAudio = useCallback(async () => {
    if (currentRecording !== null) return
    if (isRecording) {
      stopRecording()
      // onRecordingStop()
      playStop()
    } else {
      console.log("Starting recording...")
      await startRecording()
      // onRecordingStart()
      playStart()
    }
  }, [
    currentRecording,
    isRecording,
    startRecording,
    stopRecording,
    playStart,
    playStop,
  ])

  const playAudio = useCallback(() => {
    if (currentRecording === null) return
    const audio = new Audio(currentRecording)
    audio.play()
  }, [currentRecording])

  const deleteRecording = useCallback(() => {
    setCurrentRecording(null)
  }, [])

  const openAttachment = useCallback(() => {
    if (attachedImage !== null) return
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = onImageSelected
    input.click()
  }, [attachedImage])

  const onImageSelected = useCallback((event) => {
    const selectedImage = URL.createObjectURL(event.target.files[0])
    setAttachedImage(selectedImage)
  }, [])

  const deleteAttachment = useCallback(() => {
    setAttachedImage(null)
  }, [])

  const onEmojiSelected = useCallback((emoji) => {
    const input = inputRef.current
    if (input) {
      // get input cursor position
      const cursorPosition = input.selectionStart
      // get input value
      const value = input.value
      // get text before cursor
      const textBefore = value.substring(0, cursorPosition)
      // get text after cursor
      const textAfter = value.substring(cursorPosition, value.length)
      // update input value by adding the key pressed
      input.value = textBefore + emoji + " " + textAfter
      inputRef?.current?.setSelectionRange(
        cursorPosition + 2,
        cursorPosition + 2
      )
      setTimeout(() => {
        // focus input
        input.focus()
      }, 250)
    }
  }, [])

  const onSpecialKeySelected = useCallback((specialKey) => {
    const input = inputRef.current
    if (input) {
      const cursorPosition = input.selectionStart || 0
      const value = input.value || ""
      const textBefore = value.substring(0, cursorPosition)
      const textAfter = value.substring(cursorPosition, value.length)
      const newValue = textBefore + specialKey + textAfter
      input.value = newValue
      const newCursorPosition = cursorPosition + specialKey.length
      setTimeout(() => {
        input.focus()
        input.setSelectionRange(newCursorPosition, newCursorPosition)
      }, 0)
    }
  }, [])

  const onNewGradeChange = useCallback((event) => {
    const value = event.target.value
    if (!value || value === null) {
      setNewGrade("")
      return
    }
    if (value < 0 || value > 100) return
    // if value is a letter, return
    if (/[a-zA-Z]/.test(value)) return
    if (isNaN(value)) return
    setNewGrade(value)
  }, [])

  const sendTicket = useCallback(async () => {
    if (
      (inputRef?.current?.value === "" || inputRef?.current?.value === null) &&
      currentRecording === null &&
      attachedImage === null
    ) {
      return
    }
    const screenshotFile = await fetch(screenshot)
    const imageBlob = await screenshotFile.blob()
    const imageFile = new File([imageBlob], "screenshot.png", {
      type: "image/png",
    })
    let attachFile = null
    if (attachedImage !== null) {
      const attachedImageFile = await fetch(attachedImage)
      const attachedImageBlob = await attachedImageFile.blob()
      attachFile = new File([attachedImageBlob], "attachedImage.png", {
        type: "image/png",
      })
    }
    let audioFile = null
    if (currentRecording !== null) {
      const audioFileFile = await fetch(currentRecording)
      const audioFileBlob = await audioFileFile.blob()
      audioFile = new File([audioFileBlob], "audioFile.wav", {
        type: "audio/wav",
      })
    }

    const res = await addTechnicalSupport({
      groupId: selectedGroupData?.groupId,
      gradeId: studentAnswers?.gradeId,
      supportTypeId: SUPPORT_TYPES.TEACHER,
      screen: SCREEN_TYPES.CONTENT,
      comment: inputRef?.current?.value || "",
      imageFile: attachFile,
      relatedId: activity?.moduleActivityId,
      subRelatedId: exercise?.exerciseId,
      screenShot: imageFile,
      audioFile: audioFile,
      ...(viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT && {
        newGrade: newGrade,
      }),
    })
    // console.log("🚀 ~ file: index.js:165 ~ sendTicket ~ res:", res)
    if (res) {
      onClose()
      MySwal.fire(t("saved"), "", "success")
    }
  }, [
    screenshot,
    activity,
    exercise,
    selectedGroupData,
    attachedImage,
    currentRecording,
    studentAnswers,
    newGrade,
    viewMode,
  ])

  const changeGrade = useCallback(async () => {
    let audioFile = null
    if (currentRecording !== null) {
      const audioFileFile = await fetch(currentRecording)
      const audioFileBlob = await audioFileFile.blob()
      audioFile = new File([audioFileBlob], "audioFile.wav", {
        type: "audio/wav",
      })
    }
    const response = await updateGrade({
      gradeId: studentAnswers?.gradeId,
      grade: newGrade,
      comment: inputRef?.current?.value || "",
      audioFile,
    })
    if (response) {
      if (response.details) {
        const moduleProgress = response.details.moduleProgress
        const activityProgress = response.details.activityProgress
        const newActivities = [...activities]
        // update module progress
        const newModule = {
          ...module,
          ...moduleProgress,
        }
        setModule(newModule)
        newActivities[activityIndex].grade = activityProgress.grade
        newActivities[activityIndex].progress = activityProgress.progress
        newActivities[activityIndex].hasAnswersUpdatedByTeacher = true
        setActivities(newActivities)
      }
      getActivityExercises({ shouldFindNextExercise: false })
      onClose()
      MySwal.fire(t("saved"), "", "success")
    }
  }, [
    newGrade,
    studentAnswers,
    currentRecording,
    module,
    activities,
    activityIndex,
  ])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: [
          styles.dialogContent,
          fontSettings.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
        ],
      }}
      // sx={styles.dialogContent}
    >
      <Box display={"none"}>
        <TextToolBar
          updateFontStyle={(fontFamily) =>
            setFontSettings((prev) => ({ ...prev, fontFamily }))
          }
          updateFontSize={(fontSize) =>
            setFontSettings((prev) => ({ ...prev, fontSize }))
          }
          updateBgColor={(bgColor) =>
            setFontSettings((prev) => ({ ...prev, bgColor }))
          }
        />
      </Box>
      <Stack maxWidth={"95%"} width={"100%"} m={"0 auto"}>
        <DialogTitle component={"div"} mt={3.5}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={styles.closeButton}
            disableRipple
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          <Typography
            variant="h2"
            sx={[
              styles.messageHeader,
              viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT &&
                styles.messageHeaderCenter,
            ]}
            fontWeight={700}
          >
            {viewMode === ACTIVITY_VIEW_MODES.STUDENT
              ? t("message_to", {
                  name: `${selectedGroupData?.teachers[0]?.name} ${selectedGroupData?.teachers[0]?.lastName}`,
                })
              : `${userData?.name} ${userData?.lastName}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT && (
              <Stack direction={"row"} spacing={3} sx={styles.grades}>
                <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
                  <Typography color="primary">{t("current_grade")}</Typography>
                  <TextField
                    type="text"
                    min={0}
                    max={100}
                    step={1}
                    value={studentAnswers?.grade}
                    // editable={false}
                    InputProps={{ sx: styles.gradeInput, readOnly: true }}
                  />
                  <Typography color="primary">{`/100`}</Typography>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
                  <Typography color="primary">{t("new_grade")}</Typography>
                  <TextField
                    type="text"
                    min={0}
                    max={100}
                    step={1}
                    value={newGrade}
                    onChange={onNewGradeChange}
                    InputProps={{ sx: styles.gradeInput }}
                  />
                  <Typography color="primary">{`/100`}</Typography>
                </Stack>
              </Stack>
            )}
            {viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT && (
              <Typography variant="span" color="primary" fontWeight={700}>
                {t("comments")}
              </Typography>
            )}
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              spacing={1}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <KeyPicker onKeySelected={onSpecialKeySelected} />
                <EmojiPicker onEmojiSelected={onEmojiSelected} />
              </Stack>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Button
                  startIcon={
                    currentRecording !== null ? (
                      <PlayArrowIcon onClick={playAudio} />
                    ) : (
                      <MicIcon />
                    )
                  }
                  {...(isRecording && { endIcon: <StopCircleOutlinedIcon /> })}
                  {...(currentRecording !== null && {
                    endIcon: <CloseIcon onClick={deleteRecording} />,
                  })}
                  disableElevation
                  sx={[
                    styles.actionButton,
                    isRecording && styles.actionButtonRecording,
                    currentRecording !== null && styles.actionButtonRecorded,
                  ]}
                  onClick={recordAudio}
                >
                  {isRecording
                    ? t("stop")
                    : currentRecording !== null
                    ? t("saved")
                    : t("record_audio")}
                </Button>
                {viewMode === ACTIVITY_VIEW_MODES.STUDENT && (
                  <Button
                    startIcon={<AttachFileIcon />}
                    {...(attachedImage !== null && {
                      endIcon: <CloseIcon onClick={deleteAttachment} />,
                    })}
                    sx={[
                      styles.actionButton,
                      attachedImage !== null && styles.actionButtonRecorded,
                    ]}
                    onClick={openAttachment}
                  >
                    {attachedImage !== null
                      ? t("uploaded_file")
                      : t("attach_file")}
                  </Button>
                )}
              </Stack>
            </Stack>
            {viewMode === ACTIVITY_VIEW_MODES.STUDENT &&
              studentAnswers?.gradeId > 0 && (
                <Typography variant="body1">
                  {t("why_your_grade_must_be_reviewed")}
                </Typography>
              )}
            <TextField
              type="text"
              inputRef={inputRef}
              fullWidth
              multiline
              minRows={8}
              maxRows={8}
              autoFocus
              sx={styles.input}
              inputProps={{
                maxLength: 1500,
                // inputRef: inputRef,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          {viewMode === ACTIVITY_VIEW_MODES.STUDENT && (
            <Typography mr={2} variant={"body1"} sx={styles.loaded}>
              {loadingScreenshot ? t("loading") : t("loading_succesful")}
            </Typography>
          )}
          {viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT && (
            <Button
              variant="contained"
              color="danger"
              disableElevation
              sx={styles.submitButton}
              onClick={onClose}
            >
              {t("cancel")}
            </Button>
          )}
          {(viewMode === ACTIVITY_VIEW_MODES.STUDENT || newGrade > 0) && (
            <Button
              variant="contained"
              color="success"
              disableElevation
              sx={styles.submitButton}
              onClick={
                viewMode === ACTIVITY_VIEW_MODES.STUDENT
                  ? sendTicket
                  : changeGrade
              }
              loading
              disabled={loadingScreenshot || isFetching}
            >
              {viewMode === ACTIVITY_VIEW_MODES.STUDENT
                ? t("send_message")
                : t("save")}
            </Button>
          )}
        </DialogActions>
      </Stack>
    </Dialog>
  )
}
